import * as React from 'react'

import { cn } from '@/lib/utils/cn'
import { Eye, EyeOff } from 'lucide-react'
import { PasswordStrength } from '@/components/ui/password-strength'

export const PASSWORD_PLACEHOLDER = '••••••••'

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

interface InputNameProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string
}

const InputName = React.forwardRef<HTMLInputElement, InputNameProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <>
        <Input
          ref={ref}
          className={className}
          autoComplete="name"
          autoCapitalize="words"
          spellCheck={false}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </>
    )
  }
)
InputName.displayName = 'InputName'

interface InputPasswordProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  error?: string
  showStrength?: boolean
  showStrengthLabel?: boolean
  value?: string | number | readonly string[] | undefined
  defaultValue?: string | number | readonly string[] | undefined
  currentValue?: string // For controlled inputs like react-hook-form
}

const InputPassword = React.forwardRef<HTMLInputElement, InputPasswordProps>(
  (
    {
      className,
      error,
      showStrength,
      showStrengthLabel = true,
      value,
      defaultValue,
      currentValue,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)

    // Determine the password value to use for strength indicator
    const passwordValue =
      currentValue || (value ? String(value) : defaultValue ? String(defaultValue) : '')

    return (
      <>
        <div className="relative">
          <Input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            className={cn('pr-10', className)}
            aria-invalid={error ? 'true' : 'false'}
            value={value}
            defaultValue={defaultValue}
            {...props}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {showStrength && passwordValue && (
          <PasswordStrength password={passwordValue} showLabel={showStrengthLabel} />
        )}
      </>
    )
  }
)
InputPassword.displayName = 'InputPassword'

export { Input, InputName, InputPassword }
export type InputProps = React.InputHTMLAttributes<HTMLInputElement>
export type { InputNameProps, InputPasswordProps }
